import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, CloudUpload, FileIcon, Loader2, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUS_LABELS = { eingegangen: "Eingegangen", material_vollstaendig: "Material vollständig", in_produktion: "In Produktion", geplant: "Geplant", veroeffentlicht: "Veröffentlicht" };

export default function BuchungUpload() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [uploads, setUploads] = useState({});
  const [texte, setTexte] = useState({ caption: "", hashtags: "", accounts: "", link: "", terminhinweis: "", anmerkungen: "" });
  const [zuordnung, setZuordnung] = useState("post");
  const [monat, setMonat] = useState(null);
  const [posId, setPosId] = useState(null);
  const [finishing, setFinishing] = useState(false);
  const inputRef = useRef(null);

  const load = () => axios.get(`${API}/public/upload/${token}`)
    .then(({ data }) => {
      setInfo(data);
      setTexte((t) => ({ ...t, ...data.texte }));
      if (!posId && data.positionen[0]) {
        setPosId(data.positionen[0].pos_id);
        setMonat(data.positionen[0].monate?.[0] || null);
        const keys = { A: "post", B: "hauptmotiv", C: "sonstiges" };
        setZuordnung(keys[data.positionen[0].paket_key] || "sonstiges");
      }
    })
    .catch((err) => setError(err.response?.data?.detail || "Link ungültig"));
  useEffect(() => { load(); }, [token]);

  const uploadFiles = async (files) => {
    for (const file of files) {
      const uid = [...crypto.getRandomValues(new Uint8Array(16))].map((b) => b.toString(16).padStart(2, "0")).join("");
      const CH = 5 * 1024 * 1024;
      const total = Math.ceil(file.size / CH);
      setUploads((u) => ({ ...u, [uid]: { name: file.name, pct: 0 } }));
      try {
        for (let i = 0; i < total; i++) {
          const fd = new FormData();
          fd.append("upload_id", uid); fd.append("index", i);
          fd.append("chunk", file.slice(i * CH, (i + 1) * CH));
          await axios.post(`${API}/public/upload/${token}/chunk`, fd);
          setUploads((u) => ({ ...u, [uid]: { name: file.name, pct: Math.round(((i + 1) / (total + 1)) * 100) } }));
        }
        await axios.post(`${API}/public/upload/${token}/complete`, {
          upload_id: uid, filename: file.name, total, pos_id: posId, zuordnung, monat,
        });
        setUploads((u) => { const n = { ...u }; delete n[uid]; return n; });
        toast.success(`${file.name} hochgeladen ✓`);
        load();
      } catch (err) {
        setUploads((u) => { const n = { ...u }; delete n[uid]; return n; });
        toast.error(err.response?.data?.detail || `Upload von ${file.name} fehlgeschlagen`);
      }
    }
  };

  const abschliessen = async () => {
    setFinishing(true);
    try {
      await axios.post(`${API}/public/upload/${token}/texte`, texte);
      await axios.post(`${API}/public/upload/${token}/abschliessen`);
      toast.success("Material übermittelt — danke!");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
    finally { setFinishing(false); }
  };

  if (error) return (
    <div className="min-h-screen bg-[#f7f7f8] flex items-center justify-center p-6">
      <div className="text-center" data-testid="upload-error">
        <h2 className="font-head text-xl font-bold">Dieser Link ist nicht mehr gültig</h2>
        <p className="text-sm text-neutral-500 mt-2">{error}</p>
        <p className="text-sm text-neutral-500 mt-1">Melde dich gerne bei <a className="underline" href="mailto:citymanagement@innovationshafen.net">citymanagement@innovationshafen.net</a></p>
      </div>
    </div>
  );
  if (!info) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-neutral-400" /></div>;

  const aktPos = info.positionen.find((p) => p.pos_id === posId) || info.positionen[0];
  const checkPunkte = info.checkliste.filter((c) => c.pos_id === posId && (!monat || c.monat === monat || !c.monat));
  const zuordnungen = [...new Set(checkPunkte.map((c) => c.key))];
  const statusIdx = ["eingegangen", "material_vollstaendig", "in_produktion", "geplant", "veroeffentlicht"].indexOf(aktPos.status);

  return (
    <div className="min-h-screen bg-[#f7f7f8]">
      <div className="max-w-xl mx-auto px-4 py-8" data-testid="upload-portal">
        <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-neutral-400">Upload-Portal · Bruchköbel Beleben</p>
        <h1 className="font-head text-2xl font-bold mt-1">{info.firma}</h1>
        <p className="text-sm text-neutral-500">Buchung <b>{info.buchungsnummer}</b>{info.zuliefer_deadline && <> · Deadline für deine Zulieferung: <b data-testid="upload-deadline">{new Date(info.zuliefer_deadline).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" })}</b></>}</p>

        <div className="flex items-center gap-1 mt-4 overflow-x-auto" data-testid="status-track">
          {Object.entries(STATUS_LABELS).map(([k, label], i) => (
            <span key={k} className={`text-[10px] font-bold px-2.5 py-1 rounded-full shrink-0 ${i <= statusIdx ? "bg-neutral-900 text-white" : "bg-neutral-200 text-neutral-500"}`}>{label}</span>
          ))}
        </div>
        {aktPos.veroeffentlichungen?.length > 0 && aktPos.veroeffentlichungen.map((v, i) => v.url && (
          <a key={i} href={v.url} target="_blank" rel="noreferrer" className="block mt-2 text-sm font-bold text-emerald-700 underline" data-testid="published-link">→ Veröffentlichten Beitrag ansehen ({new Date(v.datum).toLocaleDateString("de-DE")})</a>
        ))}

        {info.positionen.length > 1 && (
          <div className="flex gap-2 mt-4 overflow-x-auto">
            {info.positionen.map((p) => (
              <button key={p.pos_id} onClick={() => { setPosId(p.pos_id); setMonat(p.monate?.[0] || null); }}
                className={`text-xs font-bold px-3 py-2 rounded-xl shrink-0 ${posId === p.pos_id ? "bg-neutral-900 text-white" : "bg-white border border-neutral-200"}`}>{p.paket_name}</button>
            ))}
          </div>
        )}
        {aktPos.monate?.length > 1 && (
          <div className="flex gap-2 mt-3">
            {aktPos.monate.map((m) => (
              <button key={m} data-testid={`monat-tab-${m}`} onClick={() => setMonat(m)}
                className={`text-xs font-bold px-3 py-2 rounded-xl ${monat === m ? "bg-neutral-900 text-white" : "bg-white border border-neutral-200"}`}>{m}</button>
            ))}
          </div>
        )}

        <div className="rounded-2xl border border-neutral-200 bg-white p-4 mt-4" data-testid="checkliste">
          <p className="text-xs font-bold uppercase tracking-widest text-neutral-400 mb-2">Das brauchen wir von dir</p>
          {checkPunkte.map((c, i) => (
            <div key={i} className="flex items-center justify-between py-1.5 text-sm">
              <span className={c.erfuellt ? "text-emerald-700" : "text-neutral-700"}>{c.erfuellt ? "✅" : "⬜"} {c.label}{c.monat_label ? ` (${c.monat_label})` : ""}</span>
              <span className="text-[11px] text-neutral-400">{c.ist}{c.soll ? `/${c.soll}` : ""}</span>
            </div>
          ))}
        </div>

        {!info.readonly && (
          <>
            <div className="rounded-2xl border border-neutral-200 bg-white p-4 mt-4">
              <p className="text-xs font-bold uppercase tracking-widest text-neutral-400 mb-2">Datei hochladen als …</p>
              <div className="flex flex-wrap gap-2">
                {zuordnungen.map((z) => (
                  <button key={z} data-testid={`zuordnung-${z}`} onClick={() => setZuordnung(z)}
                    className={`text-xs font-bold px-3 py-2 rounded-xl capitalize ${zuordnung === z ? "bg-neutral-900 text-white" : "bg-neutral-100"}`}>{z === "hauptmotiv" ? "Reel/Post-Motiv" : z}</button>
                ))}
              </div>
              <div data-testid="dropzone" onClick={() => inputRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); uploadFiles([...e.dataTransfer.files]); }}
                className="mt-3 border-2 border-dashed border-neutral-300 rounded-2xl p-8 text-center cursor-pointer hover:border-neutral-500 transition-colors">
                <CloudUpload className="h-8 w-8 mx-auto text-neutral-400" />
                <p className="text-sm font-bold mt-2">Dateien hierher ziehen oder tippen</p>
                <p className="text-[11px] text-neutral-400 mt-1">JPG, PNG, HEIC, WEBP, MP4, MOV, PDF, ZIP · max. 2 GB pro Datei</p>
                <input ref={inputRef} type="file" multiple hidden data-testid="upload-input"
                  accept=".jpg,.jpeg,.png,.heic,.webp,.mp4,.mov,.pdf,.zip"
                  onChange={(e) => { uploadFiles([...e.target.files]); e.target.value = ""; }} />
              </div>
              {Object.entries(uploads).map(([k, u]) => (
                <div key={k} className="mt-2 text-xs">
                  <p className="font-semibold">{u.name} — {u.pct}%</p>
                  <div className="h-1.5 bg-neutral-100 rounded-full mt-1"><div className="h-1.5 bg-neutral-900 rounded-full transition-all" style={{ width: `${u.pct}%` }} /></div>
                </div>
              ))}
            </div>

            <div className="rounded-2xl border border-neutral-200 bg-white p-4 mt-4 grid gap-3">
              <p className="text-xs font-bold uppercase tracking-widest text-neutral-400">Texte &amp; Wünsche an die Redaktion</p>
              {[["caption", "Beitragstext / Caption-Wunsch"], ["hashtags", "Hashtag-Wünsche"], ["accounts", "Zu markierende Accounts"], ["link", "Link / Call-to-Action"], ["terminhinweis", "Wunschtermin-Präzisierung"], ["anmerkungen", "Anmerkungen an die Redaktion"]].map(([k, label]) => (
                <div key={k}>
                  <label className="text-xs font-semibold text-neutral-500">{label}</label>
                  <textarea data-testid={`texte-${k}`} value={texte[k]} onChange={(e) => setTexte({ ...texte, [k]: e.target.value })} rows={k === "caption" ? 3 : 1}
                    className="w-full mt-1 px-3 py-2 rounded-xl border border-neutral-200 text-sm resize-none" />
                </div>
              ))}
            </div>
          </>
        )}

        {info.items.length > 0 && (
          <div className="rounded-2xl border border-neutral-200 bg-white p-4 mt-4" data-testid="uploaded-items">
            <p className="text-xs font-bold uppercase tracking-widest text-neutral-400 mb-2">Hochgeladene Dateien ({info.items.length})</p>
            {info.items.map((it) => (
              <div key={it.id} className="flex items-center gap-3 py-2 border-b border-neutral-50 last:border-0">
                {it.kind === "image" ? <img src={`${process.env.REACT_APP_BACKEND_URL}${it.url}?w=160`} alt="" className="h-10 w-10 rounded-lg object-cover" onError={(e) => { e.target.style.display = "none"; }} /> : <FileIcon className="h-8 w-8 text-neutral-300" />}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate">{it.dateiname}</p>
                  <p className="text-[11px] text-neutral-400 capitalize">{it.zuordnung}{it.monat ? ` · ${it.monat}` : ""}{it.status === "abgelehnt" ? ` · ❌ ${it.ablehnungsgrund || "abgelehnt"}` : it.status === "freigegeben" ? " · ✅ freigegeben" : ""}</p>
                </div>
                {!info.readonly && it.status !== "freigegeben" && (
                  <button data-testid={`item-delete-${it.id}`} onClick={async () => { await axios.delete(`${API}/public/upload/${token}/items/${it.id}`); load(); }} className="text-neutral-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                )}
              </div>
            ))}
          </div>
        )}

        {!info.readonly && info.items.length > 0 && (
          <div className="mt-5">
            <p className="text-[12px] text-neutral-500 mb-3">Mit dem Absenden bestätigst du: „Ich verfüge über alle erforderlichen Nutzungs-, Bild- und Musikrechte an den hochgeladenen Inhalten und erteile die Freigabe zur Veröffentlichung.“</p>
            <button data-testid="upload-abschliessen" disabled={finishing} onClick={abschliessen}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-4 rounded-2xl flex items-center justify-center gap-2">
              {finishing ? <Loader2 className="h-5 w-5 animate-spin" /> : <CheckCircle2 className="h-5 w-5" />} Material verbindlich übermitteln
            </button>
            {info.rechte_bestaetigt && <p className="text-[11px] text-emerald-700 mt-2 text-center">✓ Rechteerklärung bereits abgegeben — du kannst bis zur Deadline weiter ergänzen.</p>}
          </div>
        )}
      </div>
    </div>
  );
}
